
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  MenuItem,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import axios from "../../axios";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import StoreIcon from "@mui/icons-material/Store";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import EditIcon from "@mui/icons-material/Edit";

import rolesApi from "../../api/rolesApi";
import { toast } from "react-toastify";
import ResetPasswordDialog from "./ResetPasswordDialog";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function UsersPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const currentUserName = (
    localStorage.getItem("userName") || ""
  ).toLowerCase();

  const isAuthorized =
    currentUserName === "owner" ||
    currentUserName === "admin" ||
    hasPermission("User.Manage") ||
    hasPermission("Users.Manage");

  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId
    ? Number.parseInt(storedCompanyId, 10)
    : null;

  // =========================
  // البيانات
  // =========================

  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [safes, setSafes] = useState([]);
  const [stocks, setStocks] = useState([]);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // =========================
  // النوافذ
  // =========================

  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [openContextDialog, setOpenContextDialog] = useState(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] =
    useState(false);
  const [openEditUserDialog, setOpenEditUserDialog] = useState(false);

  // =========================
  // المستخدم المحدد
  // =========================

  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  // =========================
  // الأدوار والفروع والصناديق والمستودعات
  // =========================

  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedSafeIds, setSelectedSafeIds] = useState([]);
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);

  const [savingContext, setSavingContext] = useState(false);
  const [savingRoles, setSavingRoles] = useState(false);

  // =========================
  // إضافة مستخدم
  // =========================

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [branchId, setBranchId] = useState("");

  const [message, setMessage] = useState(null);
  const [severity, setSeverity] = useState("success");
  const [submitting, setSubmitting] = useState(false);

  // =========================
  // تعديل مستخدم
  // =========================

  const [editUserName, setEditUserName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // =========================
  // جلب الفروع
  // =========================

  useEffect(() => {
    if (!companyId) {
      setBranches([]);
      return undefined;
    }

    let active = true;

    const loadBranches = async () => {
      setLoadingBranches(true);

      try {
        const res = await axios.get(
          `/Branch/by-company/${companyId}`
        );

        if (active) {
          setBranches(
            Array.isArray(res.data) ? res.data : []
          );
        }
      } catch (err) {
        console.error("فشل في جلب الفروع:", err);

        if (active) {
          toast.error("تعذر تحميل الفروع");
        }
      } finally {
        if (active) {
          setLoadingBranches(false);
        }
      }
    };

    loadBranches();

    return () => {
      active = false;
    };
  }, [companyId]);

  // =========================
  // جلب المستخدمين
  // =========================

  const fetchUsers = useCallback(async () => {
    if (!companyId) {
      setUsers([]);
      return;
    }

    setLoadingUsers(true);

    try {
      const res = await axios.get(
        `/User/AllUser/${companyId}`
      );

      if (Array.isArray(res.data?.users)) {
        setUsers(res.data.users);
      } else if (Array.isArray(res.data)) {
        setUsers(res.data);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("فشل في جلب المستخدمين:", err);
      setUsers([]);
      toast.error("تعذر تحميل المستخدمين");
    } finally {
      setLoadingUsers(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // =========================
  // جلب الأدوار والصناديق والمستودعات
  // =========================

  useEffect(() => {
    if (!companyId) {
      setRoles([]);
      setSafes([]);
      setStocks([]);
      return undefined;
    }

    let active = true;

    const loadResources = async () => {
      try {
        const [rolesRes, safesRes, stocksRes] =
          await Promise.allSettled([
            rolesApi.getAll(companyId),
            axios.get(`/Safe/by-company/${companyId}`),
            axios.get(`/Stock/by-company/${companyId}`),
          ]);

        if (!active) return;

        if (rolesRes.status === "fulfilled") {
          setRoles(
            Array.isArray(rolesRes.value.data)
              ? rolesRes.value.data
              : []
          );
        } else {
          console.error(
            "فشل تحميل الأدوار:",
            rolesRes.reason
          );
        }

        if (safesRes.status === "fulfilled") {
          setSafes(
            Array.isArray(safesRes.value.data)
              ? safesRes.value.data
              : []
          );
        } else {
          console.error(
            "فشل تحميل الصناديق:",
            safesRes.reason
          );
        }

        if (stocksRes.status === "fulfilled") {
          setStocks(
            Array.isArray(stocksRes.value.data)
              ? stocksRes.value.data
              : []
          );
        } else {
          console.error(
            "فشل تحميل المستودعات:",
            stocksRes.reason
          );
        }
      } catch (err) {
        console.error("فشل تحميل بيانات الصفحة:", err);
      }
    };

    loadResources();

    return () => {
      active = false;
    };
  }, [companyId]);

  // =========================
  // أدوات مساعدة
  // =========================

  const getUserBranchId = (user) => {
    const id =
      user?.branchId ??
      user?.branch?.id ??
      user?.branch?.branchId ??
      user?.assignment?.branchId;

    return id === null || id === undefined ? "" : String(id);
  };

  const normalizeIds = (values) =>
    Array.isArray(values)
      ? values
          .map(Number)
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];

  const getErrorMessage = (err, fallback) =>
    err.response?.data?.message ||
    err.response?.data?.title ||
    err.message ||
    fallback;

  // =========================
  // فتح نافذة إضافة مستخدم
  // =========================

  const handleOpenAddUser = () => {
    setMessage(null);
    setSeverity("success");

    setUserName("");
    setPassword("");
    setMobile("");
    setBranchId("");

    setOpenAddUserDialog(true);
  };

  // =========================
  // فتح نافذة تعديل المستخدم
  // =========================

  const handleOpenEditUser = (user) => {
    setEditingUser(user);

    setEditUserName(user?.userName || "");
    setEditMobile(user?.mobile || "");
    setEditEmail(user?.email || "");
    setEditBranchId(getUserBranchId(user));
    setEditPassword("");

    setOpenEditUserDialog(true);
  };

  // =========================
  // حفظ تعديل المستخدم
  // =========================

  const handleSaveEditUser = async (e) => {
    e.preventDefault();

    if (!editingUser) {
      toast.error("لم يتم تحديد المستخدم");
      return;
    }

    if (
      !editUserName.trim() ||
      !editMobile.trim() ||
      !editBranchId
    ) {
      toast.error(
        "يرجى إدخال اسم المستخدم ورقم الجوال واختيار الفرع"
      );
      return;
    }

    const selectedBranch = branches.find(
      (branch) =>
        Number(branch.id) === Number(editBranchId)
    );

    if (!selectedBranch) {
      toast.error("الفرع المحدد غير موجود ضمن فروع الشركة");
      return;
    }

    setSavingEdit(true);

    try {
      // يتوافق هذا الطلب مع PUT /User/UpdateUser
      // يجب أن يكون هذا المسار معرفًا في UserController.
      const payload = {
        userId: Number(editingUser.id),
        userName: editUserName.trim(),
        mobile: editMobile.trim(),
        email: editEmail.trim(),
        branchId: Number(editBranchId),
      };

      // لا نرسل كلمة المرور إلا عند إدخال كلمة مرور جديدة.
      if (editPassword.trim()) {
        payload.password = editPassword.trim();
      }

      const response = await axios.put(
        "/User/UpdateUser",
        payload
      );

      if (response.data?.success === false) {
        throw new Error(
          response.data.message ||
            "فشل تحديث بيانات المستخدم"
        );
      }

      toast.success(
        response.data?.message ||
          "تم تحديث بيانات المستخدم بنجاح"
      );

      setOpenEditUserDialog(false);
      setEditingUser(null);
      setEditPassword("");

      await fetchUsers();
    } catch (err) {
      const serverMessage = getErrorMessage(
        err,
        "فشل تحديث بيانات المستخدم"
      );

      toast.error(serverMessage);
      console.error("Update user failed:", err);
    } finally {
      setSavingEdit(false);
    }
  };

  // =========================
  // إنشاء مستخدم
  // =========================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage(null);

    if (
      !userName.trim() ||
      !password.trim() ||
      !mobile.trim() ||
      !branchId
    ) {
      setSeverity("error");
      setMessage("يرجى تعبئة جميع الحقول المطلوبة");
      return;
    }

    const selectedBranch = branches.find(
      (branch) => Number(branch.id) === Number(branchId)
    );

    if (!selectedBranch) {
      setSeverity("error");
      setMessage("يرجى اختيار فرع صحيح");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post("/User/register", {
        userName: userName.trim(),
        password: password.trim(),
        mobile: mobile.trim(),
        branchId: Number(branchId),
      });

      if (response.data?.success === false) {
        throw new Error(
          response.data.message ||
            "فشل في إنشاء المستخدم"
        );
      }

      const successMessage =
        response.data?.message ||
        "تم إنشاء المستخدم بنجاح";

      setSeverity("success");
      setMessage(successMessage);

      toast.success(successMessage);

      setUserName("");
      setPassword("");
      setMobile("");
      setBranchId("");

      setOpenAddUserDialog(false);

      await fetchUsers();
    } catch (err) {
      const serverMessage = getErrorMessage(
        err,
        "فشل في إنشاء المستخدم"
      );

      setSeverity("error");
      setMessage(serverMessage);
      toast.error(serverMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // =========================
  // فتح نافذة إدارة الأدوار
  // =========================

  const handleOpenRoleDialog = (user) => {
    setSelectedUser(user);

    const currentRoleIds = Array.isArray(user?.roles)
      ? user.roles
          .map((role) =>
            Number(role.roleId ?? role.id)
          )
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];

    setSelectedRoleIds(currentRoleIds);
    setOpenRoleDialog(true);
  };

  // =========================
  // حفظ الأدوار
  // =========================

  const handleSaveRoles = async () => {
    if (!selectedUser) return;

    setSavingRoles(true);

    try {
      const response = await axios.post(
        "/User/AssignRoles",
        {
          userId: Number(selectedUser.id),
          roleIds: normalizeIds(selectedRoleIds),
        }
      );

      if (response.data?.success === false) {
        throw new Error(
          response.data.message ||
            "فشل تحديث أدوار المستخدم"
        );
      }

      toast.success(
        response.data?.message ||
          "تم تحديث أدوار المستخدم بنجاح"
      );

      setOpenRoleDialog(false);

      await fetchUsers();
    } catch (err) {
      toast.error(
        getErrorMessage(err, "فشل تحديث الأدوار")
      );
    } finally {
      setSavingRoles(false);
    }
  };

  // =========================
  // فتح نافذة ربط الموارد
  // =========================

  const handleOpenContextDialog = (user) => {
    setSelectedUser(user);

    setSelectedSafeIds(
      normalizeIds(user?.allowedSafes)
    );

    setSelectedStockIds(
      normalizeIds(user?.allowedStocks)
    );

    setSelectedBranchIds(
      normalizeIds(user?.allowedBranches)
    );

    setOpenContextDialog(true);
  };

  // =========================
  // حفظ ربط الموارد
  // =========================

  const handleSaveContext = async () => {
    if (!selectedUser) return;

    setSavingContext(true);

    try {
      const response = await axios.post(
        "/User/AssignContext",
        {
          userId: Number(selectedUser.id),
          safeIds: normalizeIds(selectedSafeIds),
          stockIds: normalizeIds(selectedStockIds),
          branchIds: normalizeIds(selectedBranchIds),
        }
      );

      if (response.data?.success === false) {
        throw new Error(
          response.data.message ||
            "فشل تحديث ارتباطات المستخدم"
        );
      }

      toast.success(
        response.data?.message ||
          "تم تحديث ارتباطات المستخدم بنجاح"
      );

      setOpenContextDialog(false);

      await fetchUsers();
    } catch (err) {
      toast.error(
        getErrorMessage(
          err,
          "فشل تحديث الارتباطات"
        )
      );

      console.error("Assign context failed:", err);
    } finally {
      setSavingContext(false);
    }
  };

  // =========================
  // فتح نافذة إعادة تعيين كلمة المرور
  // =========================

  const handleOpenResetPassword = (user) => {
    setSelectedUser(user);
    setOpenResetPasswordDialog(true);
  };

  // =========================
  // أعمدة الجدول
  // =========================

  const columns = [
    {
      field: "id",
      headerName: "المعرّف",
      width: 85,
    },
    {
      field: "userName",
      headerName: "اسم المستخدم",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "mobile",
      headerName: "رقم الجوال",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "companyName",
      headerName: "الشركة",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "branchName",
      headerName: "الفرع الأساسي",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "roles",
      headerName: "الأدوار",
      flex: 1.5,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          gap={0.5}
          flexWrap="wrap"
          alignItems="center"
          sx={{ py: 0.5 }}
        >
          {(params.value || []).map((roleName, idx) => (
            <Chip
              key={`${roleName}-${idx}`}
              label={roleName}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "الإجراءات",
      width: 190,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const user = params.row.originalUser;

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0}
          >
            <Tooltip title="تعديل بيانات المستخدم">
              <IconButton
                color="info"
                onClick={() => handleOpenEditUser(user)}
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="إدارة الأدوار">
              <IconButton
                color="primary"
                onClick={() => handleOpenRoleDialog(user)}
                size="small"
              >
                <SecurityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="ربط الفروع والصناديق والمستودعات">
              <IconButton
                color="secondary"
                onClick={() => handleOpenContextDialog(user)}
                size="small"
              >
                <StoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="إعادة تعيين كلمة المرور">
              <IconButton
                color="warning"
                onClick={() => handleOpenResetPassword(user)}
                size="small"
              >
                <VpnKeyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const rows = users.map((user) => ({
    id: user.id,
    userName: user.userName || "",
    mobile: user.mobile || "",
    companyName: user.company?.name || "—",
    branchName: user.branch?.name || "—",
    roles: Array.isArray(user.roles)
      ? user.roles
          .map((role) => role.name || role.role?.name)
          .filter(Boolean)
      : [],
    originalUser: user,
  }));

  // =========================
  // التحقق من الصلاحيات
  // =========================

  if (!isAuthorized) {
    return (
      <Box
        p={3}
        dir="rtl"
        textAlign="center"
        mt={5}
      >
        <SecurityIcon
          sx={{
            fontSize: 60,
            color: "text.secondary",
            mb: 2,
          }}
        />

        <Typography
          variant="h5"
          color="text.secondary"
          fontWeight="bold"
        >
          عذراً، ليس لديك صلاحية للوصول إلى هذه الصفحة.
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          mt={1}
        >
          هذه الصفحة مخصصة للمدير العام أو من يملك صلاحية
          إدارة المستخدمين.
        </Typography>
      </Box>
    );
  }

  // =========================
  // واجهة الصفحة
  // =========================

  return (
    <Container
      maxWidth="xl"
      sx={{ py: { xs: 2, sm: 3 } }}
      dir="rtl"
    >
      {/* عنوان الصفحة */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          px: { xs: 1, sm: 2 },
          py: 1.5,
          mb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <PeopleIcon
            sx={{ fontSize: 26, color: "primary.main" }}
          />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: "text.primary",
              fontSize: { xs: "1rem", sm: "1.2rem" },
            }}
          >
            إدارة المستخدمين
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenAddUser}
            size="small"
            sx={{ fontWeight: 700 }}
          >
            إضافة مستخدم
          </Button>

          <Button
            variant="outlined"
            startIcon={<SecurityIcon />}
            onClick={() => navigate("/roles")}
            size="small"
            sx={{ fontWeight: 700 }}
          >
            إدارة الأدوار
          </Button>
        </Box>
      </Box>

      {/* جدول المستخدمين */}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Box
          p={2}
          borderBottom={1}
          borderColor="divider"
          bgcolor="background.default"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <PeopleIcon color="primary" />

            <Typography variant="h6" fontWeight="bold">
              قائمة الموظفين
            </Typography>
          </Box>

          <Chip
            label={`${users.length} مستخدم`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: "bold", borderRadius: 2 }}
          />
        </Box>

        <Box sx={{ height: 600, width: "100%", p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loadingUsers}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: "لا يوجد مستخدمون",
              footerRowSelected: (count) =>
                `تم تحديد ${count} صف`,
              footerTotalRows: "إجمالي الصفوف:",
              columnMenuLabel: "القائمة",
              columnMenuSortAsc: "ترتيب تصاعدي",
              columnMenuSortDesc: "ترتيب تنازلي",
              columnMenuFilter: "تصفية",
              columnMenuHideColumn: "إخفاء العمود",
              columnMenuShowColumns: "إظهار الأعمدة",
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
                color: "text.secondary",
                fontSize: "0.95rem",
              },
              "& .MuiDataGrid-columnHeader": {
                fontWeight: 700,
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  bgcolor: "action.hover",
                },
                borderBottom: 1,
                borderColor: "divider",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
            }}
          />
        </Box>
      </Paper>

      {/* =====================================
          نافذة إضافة مستخدم
      ===================================== */}

      <Dialog
        open={openAddUserDialog}
        onClose={() => {
          if (!submitting) {
            setOpenAddUserDialog(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle
          sx={{
            bgcolor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
            fontWeight: "bold",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddIcon color="primary" />
            إضافة مستخدم جديد
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 1 }}>
          {message && (
            <Alert severity={severity} sx={{ mb: 2, mt: 1 }}>
              {message}
            </Alert>
          )}

          <Box
            component="form"
            id="addUserForm"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              fullWidth
              label="اسم المستخدم"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
            />

            <TextField
              margin="normal"
              fullWidth
              label="رقم الجوال"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              required
              placeholder="05xxxxxxxx"
            />

            <TextField
              margin="normal"
              fullWidth
              label="كلمة المرور"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />

            <TextField
              margin="normal"
              select
              fullWidth
              label="اختر الفرع"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              required
              disabled={loadingBranches}
            >
              <MenuItem value="">
                <em>— اختر فرعاً —</em>
              </MenuItem>

              {branches.map((branch) => (
                <MenuItem
                  key={branch.id}
                  value={String(branch.id)}
                >
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Button
            onClick={() => setOpenAddUserDialog(false)}
            color="inherit"
            disabled={submitting}
          >
            إلغاء
          </Button>

          <Button
            type="submit"
            form="addUserForm"
            variant="contained"
            disabled={submitting || loadingBranches}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PersonAddIcon />
              )
            }
          >
            {submitting ? "جارٍ الحفظ..." : "حفظ المستخدم"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================
          نافذة تعديل المستخدم
      ===================================== */}

      <Dialog
        open={openEditUserDialog}
        onClose={() => {
          if (!savingEdit) {
            setOpenEditUserDialog(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle
          sx={{
            bgcolor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
            fontWeight: "bold",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            تعديل بيانات المستخدم
          </Box>
        </DialogTitle>

        <Box
          component="form"
          onSubmit={handleSaveEditUser}
        >
          <DialogContent sx={{ p: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              رقم المستخدم: {editingUser?.id ?? "—"}
            </Typography>

            <TextField
              fullWidth
              margin="normal"
              label="اسم المستخدم"
              value={editUserName}
              onChange={(e) =>
                setEditUserName(e.target.value)
              }
              required
              autoComplete="username"
            />

            <TextField
              fullWidth
              margin="normal"
              label="رقم الجوال"
              value={editMobile}
              onChange={(e) =>
                setEditMobile(e.target.value)
              }
              required
              inputProps={{ inputMode: "tel" }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="البريد الإلكتروني"
              type="email"
              value={editEmail}
              onChange={(e) =>
                setEditEmail(e.target.value)
              }
            />

            <TextField
              fullWidth
              select
              margin="normal"
              label="الفرع الأساسي"
              value={editBranchId}
              onChange={(e) =>
                setEditBranchId(e.target.value)
              }
              required
              disabled={loadingBranches}
            >
              <MenuItem value="">
                <em>اختر الفرع</em>
              </MenuItem>

              {branches.map((branch) => (
                <MenuItem
                  key={branch.id}
                  value={String(branch.id)}
                >
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              margin="normal"
              label="كلمة المرور الجديدة (اختياري)"
              type="password"
              value={editPassword}
              onChange={(e) =>
                setEditPassword(e.target.value)
              }
              autoComplete="new-password"
              helperText="اترك الحقل فارغاً للاحتفاظ بكلمة المرور الحالية."
            />
          </DialogContent>

          <DialogActions
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Button
              onClick={() => setOpenEditUserDialog(false)}
              color="inherit"
              disabled={savingEdit}
            >
              إلغاء
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={savingEdit || loadingBranches}
              startIcon={
                savingEdit ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <EditIcon />
                )
              }
            >
              {savingEdit
                ? "جارٍ الحفظ..."
                : "حفظ التعديلات"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* =====================================
          نافذة إدارة الأدوار
      ===================================== */}

      <Dialog
        open={openRoleDialog}
        onClose={() => {
          if (!savingRoles) {
            setOpenRoleDialog(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          إدارة أدوار المستخدم: {selectedUser?.userName}
        </DialogTitle>

        <DialogContent dividers>
          <FormControl sx={{ my: 1 }} fullWidth>
            <InputLabel id="user-roles-label">
              الأدوار
            </InputLabel>

            <Select
              labelId="user-roles-label"
              multiple
              value={selectedRoleIds}
              onChange={(e) => {
                const value = e.target.value;

                setSelectedRoleIds(
                  normalizeIds(
                    typeof value === "string"
                      ? value.split(",")
                      : value
                  )
                );
              }}
              input={<OutlinedInput label="الأدوار" />}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {selected.map((id) => (
                    <Chip
                      key={id}
                      label={
                        roles.find(
                          (role) =>
                            Number(role.id) === Number(id)
                        )?.name || id
                      }
                    />
                  ))}
                </Box>
              )}
            >
              {roles.map((role) => (
                <MenuItem
                  key={role.id}
                  value={Number(role.id)}
                >
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenRoleDialog(false)}
            color="inherit"
            disabled={savingRoles}
          >
            إلغاء
          </Button>

          <Button
            onClick={handleSaveRoles}
            variant="contained"
            disabled={savingRoles}
            startIcon={
              savingRoles ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {savingRoles ? "جارٍ الحفظ..." : "حفظ الأدوار"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================
          نافذة ربط الفروع والصناديق والمستودعات
      ===================================== */}

      <Dialog
        open={openContextDialog}
        onClose={() => {
          if (!savingContext) {
            setOpenContextDialog(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle
          sx={{
            bgcolor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
            fontWeight: "bold",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <StoreIcon color="secondary" />
            ربط الموارد: {selectedUser?.userName}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            عند مسح جميع الاختيارات، يعتمد السماح بالوصول إلى
            الموارد على طريقة معالجة القوائم الفارغة في الخادم.
          </Alert>

          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            mt={1}
          >
            {/* الفروع */}

            <FormControl fullWidth>
              <InputLabel id="branches-select-label">
                الفروع المسموح بها
              </InputLabel>

              <Select
                labelId="branches-select-label"
                multiple
                value={selectedBranchIds}
                onChange={(e) => {
                  const value = e.target.value;

                  setSelectedBranchIds(
                    normalizeIds(
                      typeof value === "string"
                        ? value.split(",")
                        : value
                    )
                  );
                }}
                input={
                  <OutlinedInput label="الفروع المسموح بها" />
                }
                renderValue={(selected) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                    }}
                  >
                    {selected.length === 0 ? (
                      <Chip
                        label="غير محدد"
                        color="success"
                        size="small"
                      />
                    ) : (
                      selected.map((id) => (
                        <Chip
                          key={id}
                          label={
                            branches.find(
                              (branch) =>
                                Number(branch.id) === Number(id)
                            )?.name || id
                          }
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                )}
              >
                {branches.map((branch) => (
                  <MenuItem
                    key={branch.id}
                    value={Number(branch.id)}
                  >
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>

              <Button
                size="small"
                sx={{ alignSelf: "flex-end", mt: 0.5 }}
                onClick={() => setSelectedBranchIds([])}
                disabled={
                  savingContext ||
                  selectedBranchIds.length === 0
                }
              >
                مسح تحديد الفروع
              </Button>
            </FormControl>

            {/* الصناديق */}

            <FormControl fullWidth>
              <InputLabel id="safes-select-label">
                الصناديق المسموح بها
              </InputLabel>

              <Select
                labelId="safes-select-label"
                multiple
                value={selectedSafeIds}
                onChange={(e) => {
                  const value = e.target.value;

                  setSelectedSafeIds(
                    normalizeIds(
                      typeof value === "string"
                        ? value.split(",")
                        : value
                    )
                  );
                }}
                input={
                  <OutlinedInput label="الصناديق المسموح بها" />
                }
                renderValue={(selected) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                    }}
                  >
                    {selected.length === 0 ? (
                      <Chip
                        label="غير محدد"
                        color="success"
                        size="small"
                      />
                    ) : (
                      selected.map((id) => (
                        <Chip
                          key={id}
                          label={
                            safes.find(
                              (safe) =>
                                Number(safe.safeId ?? safe.id) ===
                                Number(id)
                            )?.name || id
                          }
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                )}
              >
                {safes.map((safe) => {
                  const id = Number(safe.safeId ?? safe.id);

                  return (
                    <MenuItem key={id} value={id}>
                      {safe.name}
                    </MenuItem>
                  );
                })}
              </Select>

              <Button
                size="small"
                sx={{ alignSelf: "flex-end", mt: 0.5 }}
                onClick={() => setSelectedSafeIds([])}
                disabled={
                  savingContext ||
                  selectedSafeIds.length === 0
                }
              >
                مسح تحديد الصناديق
              </Button>
            </FormControl>

            {/* المستودعات */}

            <FormControl fullWidth>
              <InputLabel id="stocks-select-label">
                المستودعات المسموح بها
              </InputLabel>

              <Select
                labelId="stocks-select-label"
                multiple
                value={selectedStockIds}
                onChange={(e) => {
                  const value = e.target.value;

                  setSelectedStockIds(
                    normalizeIds(
                      typeof value === "string"
                        ? value.split(",")
                        : value
                    )
                  );
                }}
                input={
                  <OutlinedInput label="المستودعات المسموح بها" />
                }
                renderValue={(selected) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                    }}
                  >
                    {selected.length === 0 ? (
                      <Chip
                        label="غير محدد"
                        color="success"
                        size="small"
                      />
                    ) : (
                      selected.map((id) => (
                        <Chip
                          key={id}
                          label={
                            stocks.find(
                              (stock) =>
                                Number(stock.id ?? stock.stockId) ===
                                Number(id)
                            )?.name || id
                          }
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                )}
              >
                {stocks.map((stock) => {
                  const id = Number(stock.id ?? stock.stockId);

                  return (
                    <MenuItem key={id} value={id}>
                      {stock.name}
                    </MenuItem>
                  );
                })}
              </Select>

              <Button
                size="small"
                sx={{ alignSelf: "flex-end", mt: 0.5 }}
                onClick={() => setSelectedStockIds([])}
                disabled={
                  savingContext ||
                  selectedStockIds.length === 0
                }
              >
                مسح تحديد المستودعات
              </Button>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Button
            onClick={() => setOpenContextDialog(false)}
            color="inherit"
            disabled={savingContext}
          >
            إلغاء
          </Button>

          <Button
            onClick={handleSaveContext}
            variant="contained"
            color="secondary"
            disabled={savingContext}
            startIcon={
              savingContext ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <StoreIcon />
              )
            }
          >
            {savingContext ? "جارٍ الحفظ..." : "حفظ الربط"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================
          نافذة إعادة تعيين كلمة المرور
      ===================================== */}

      <ResetPasswordDialog
        open={openResetPasswordDialog}
        onClose={() => setOpenResetPasswordDialog(false)}
        user={selectedUser}
      />
    </Container>
  );
}
